import { useState, useMemo, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useTasks } from "@/hooks/useTasks";
import { useLeads } from "@/hooks/useLeads";
import { useBankers } from "@/hooks/useBankers";
import { useChannelPartners } from "@/hooks/useChannelPartners";
import { getLegacyCustomerName, LOAN_TYPE_LABELS, Task } from "@/types";
import { Bell, BellPlus, CheckCircle2, Calendar, Search, AlertTriangle, Clock, CalendarClock, ChevronRight, Check, RotateCcw, SlidersHorizontal, User, Users2, Phone, MessageCircle, Paperclip } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { format, isToday, isTomorrow, isPast, isFuture, parseISO, formatDistanceToNow, isThisWeek } from "date-fns";
import { cn } from "@/lib/utils";
import { AddTaskDialog } from "@/components/dashboard/AddTaskDialog";
import { ViewEditTaskDialog } from "@/components/dashboard/ViewEditTaskDialog";
import { toast } from "sonner";
import { useLocation } from "react-router-dom";
import { searchItems } from "@/lib/searchUtils";
import { useModal } from "@/contexts/ModalContext";
import { getAllActiveUsers } from "@/services/userService";

type TimeFilter = "all" | "overdue" | "today" | "week" | "upcoming" | "dateRange";
type OwnerFilter = "all" | "personal" | "team";
type UrgencyGroup = "overdue" | "today" | "upcoming";

const URGENCY_CONFIG: Record<UrgencyGroup, { label: string; icon: typeof AlertTriangle; color: string; badgeVariant: string; borderColor: string; bgColor: string }> = {
  overdue: {
    label: "Overdue",
    icon: AlertTriangle,
    color: "text-red-600 dark:text-red-400",
    badgeVariant: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
    borderColor: "border-l-red-500",
    bgColor: "bg-red-50/50 dark:bg-red-950/20",
  },
  today: {
    label: "Due Today",
    icon: Clock,
    color: "text-orange-600 dark:text-orange-400",
    badgeVariant: "bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300",
    borderColor: "border-l-orange-500",
    bgColor: "bg-orange-50/50 dark:bg-orange-950/20",
  },
  upcoming: {
    label: "Upcoming",
    icon: CalendarClock,
    color: "text-blue-600 dark:text-blue-400",
    badgeVariant: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
    borderColor: "border-l-blue-500",
    bgColor: "bg-blue-50/50 dark:bg-blue-950/20",
  },
};

function getUrgencyGroup(task: Task): UrgencyGroup {
  if (!task.dueDate) return "upcoming";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  if (isToday(dueDate)) return "today";
  if (isPast(dueDate)) return "overdue";
  return "upcoming";
}

function getRelativeTime(task: Task): string {
  if (!task.dueDate) return "";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  
  if (isToday(dueDate)) {
    const timeStr = format(dueDate, "h:mm a");
    return `Today at ${timeStr}`;
  }
  if (isTomorrow(dueDate)) {
    const timeStr = format(dueDate, "h:mm a");
    return `Tomorrow at ${timeStr}`;
  }
  if (isPast(dueDate)) {
    return `${formatDistanceToNow(dueDate)} overdue`;
  }
  return `Due ${formatDistanceToNow(dueDate, { addSuffix: true })}`;
}

function getFormattedDate(task: Task): string {
  if (!task.dueDate) return "";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  return format(dueDate, "MMM d, yyyy");
}

function getFormattedTime(task: Task): string {
  if (!task.dueDate) return "";
  const dueDate = typeof task.dueDate === 'string' ? parseISO(task.dueDate) : new Date(task.dueDate);
  return format(dueDate, "h:mm a");
}

export default function Tasks() {
  const location = useLocation();
  const { tasks, updateTask, deleteTask } = useTasks();
  const { leads } = useLeads();
  const { bankers } = useBankers();
  const { partners: channelPartners } = useChannelPartners();
  const [addTaskOpen, setAddTaskOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [activeTab, setActiveTab] = useState<"pending" | "completed">("pending");
  const [searchQuery, setSearchQuery] = useState("");
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("all");
  const [ownerFilter, setOwnerFilter] = useState<OwnerFilter>("all");
  const [dateRangeStart, setDateRangeStart] = useState<string>("");
  const [dateRangeEnd, setDateRangeEnd] = useState<string>("");
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false);
  const [taskToComplete, setTaskToComplete] = useState<string | null>(null);
  const [confirmUncompleteOpen, setConfirmUncompleteOpen] = useState(false);
  const [taskToUncomplete, setTaskToUncomplete] = useState<string | null>(null);
  const { registerModal, unregisterModal } = useModal();
  useEffect(() => {
    if (!confirmCompleteOpen) return;
    registerModal('task-confirm-complete', () => setConfirmCompleteOpen(false), 2);
    return () => unregisterModal('task-confirm-complete');
  }, [confirmCompleteOpen, registerModal, unregisterModal]);
  useEffect(() => {
    if (!confirmUncompleteOpen) return;
    registerModal('task-confirm-uncomplete', () => setConfirmUncompleteOpen(false), 2);
    return () => unregisterModal('task-confirm-uncomplete');
  }, [confirmUncompleteOpen, registerModal, unregisterModal]);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);

  useEffect(() => {
    getAllActiveUsers().then(users => setAvailableUsers(users)).catch(() => {});
  }, []);

  const getCreatorFirstName = (task: Task): string => {
    if (task.createdByName) return task.createdByName.split(' ')[0];
    if (!task.createdBy) return '';
    const name = task.createdBy;
    if (name.includes('user_') || (name.length > 20 && !name.includes(' '))) {
      const found = availableUsers.find(u => (u.userId || u.id) === name);
      return found?.name?.split(' ')[0] || '';
    }
    return name.split(' ')[0];
  };

  // Handle opening task from notification
  useEffect(() => {
    const openTaskId = (location.state as any)?.openTaskId;
    if (openTaskId && tasks.length > 0) {
      const task = tasks.find(t => t.id === openTaskId);
      if (task) {
        setSelectedTask(task);
        window.history.replaceState({}, document.title);
      }
    }
  }, [location.state, tasks]);

  const getLeadForTask = (task: Task) => {
    return leads.find(l => l.id === task.leadId);
  };

  // Filter tasks
  const filteredTasks = useMemo(() => {
    let filtered = tasks.filter(t => t.type === 'reminder');

    // Search filter - enhanced with multi-field search
    if (searchQuery.trim()) {
      const searchFields = filtered.map(t => ({
        ...t,
        leadName: getLeadForTask(t) ? getLegacyCustomerName(getLeadForTask(t)!) : '',
      }));
      
      filtered = searchItems(
        searchQuery,
        searchFields,
        ['title', 'description', 'leadName', 'priority']
      ).map(({ leadName, ...task }) => task);
    }

    return filtered;
  }, [tasks, searchQuery, leads]);

  const pendingTasks = useMemo(() => {
    let pending = filteredTasks.filter(t => !t.completed);

    // Owner filter
    if (ownerFilter !== "all") {
      if (ownerFilter === "personal") {
        pending = pending.filter(t => !t.sharedWith || t.sharedWith.length === 0);
      } else if (ownerFilter === "team") {
        pending = pending.filter(t => t.sharedWith && t.sharedWith.length > 0);
      }
    }

    // Time filter
    if (timeFilter === "dateRange" && (dateRangeStart || dateRangeEnd)) {
      pending = pending.filter(t => {
        if (!t.dueDate) return false;
        const dueDate = typeof t.dueDate === 'string' ? parseISO(t.dueDate) : new Date(t.dueDate);
        const dueDateStr = format(dueDate, "yyyy-MM-dd");
        
        if (dateRangeStart && dateRangeEnd) {
          return dueDateStr >= dateRangeStart && dueDateStr <= dateRangeEnd;
        } else if (dateRangeStart) {
          return dueDateStr >= dateRangeStart;
        } else if (dateRangeEnd) {
          return dueDateStr <= dateRangeEnd;
        }
        return true;
      });
    } else if (timeFilter !== "all") {
      pending = pending.filter(t => {
        if (!t.dueDate) return timeFilter === "upcoming";
        const dueDate = typeof t.dueDate === 'string' ? parseISO(t.dueDate) : new Date(t.dueDate);
        switch (timeFilter) {
          case "overdue": return isPast(dueDate) && !isToday(dueDate);
          case "today": return isToday(dueDate);
          case "week": return isThisWeek(dueDate);
          case "upcoming": return isFuture(dueDate) && !isToday(dueDate);
          default: return true;
        }
      });
    }

    // Sort by urgency then date
    return pending.sort((a, b) => {
      const urgencyOrder: Record<UrgencyGroup, number> = { overdue: 0, today: 1, upcoming: 2 };
      const urgencyA = urgencyOrder[getUrgencyGroup(a)];
      const urgencyB = urgencyOrder[getUrgencyGroup(b)];
      if (urgencyA !== urgencyB) return urgencyA - urgencyB;
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : Infinity;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : Infinity;
      return dateA - dateB;
    });
  }, [filteredTasks, timeFilter, ownerFilter, dateRangeStart, dateRangeEnd]);

  const completedTasks = useMemo(() => {
    let completed = filteredTasks.filter(t => t.completed);
    
    // Owner filter
    if (ownerFilter !== "all") {
      if (ownerFilter === "personal") {
        completed = completed.filter(t => !t.sharedWith || t.sharedWith.length === 0);
      } else if (ownerFilter === "team") {
        completed = completed.filter(t => t.sharedWith && t.sharedWith.length > 0);
      }
    }
    
    return completed.sort((a, b) => {
      const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
      const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
      return dateB - dateA;
    });
  }, [filteredTasks, ownerFilter]);

  // Group pending tasks
  const grouped = useMemo(() => {
    return pendingTasks.reduce<Record<UrgencyGroup, Task[]>>(
      (acc, task) => {
        const group = getUrgencyGroup(task);
        acc[group].push(task);
        return acc;
      },
      { overdue: [], today: [], upcoming: [] }
    );
  }, [pendingTasks]);

  const handleCompleteRequest = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setTaskToComplete(taskId);
    setConfirmCompleteOpen(true);
  };

  const handleConfirmComplete = async () => {
    if (taskToComplete) {
      await updateTask(taskToComplete, { completed: true });
      toast.success("Reminder completed");
    }
    setConfirmCompleteOpen(false);
    setTaskToComplete(null);
  };

  const handleUnmarkCompleteRequest = (e: React.MouseEvent, taskId: string) => {
    e.stopPropagation();
    setTaskToUncomplete(taskId);
    setConfirmUncompleteOpen(true);
  };

  const handleConfirmUncomplete = async () => {
    if (taskToUncomplete) {
      await updateTask(taskToUncomplete, { completed: false });
      toast.success("Reminder moved to pending");
    }
    setConfirmUncompleteOpen(false);
    setTaskToUncomplete(null);
  };

  const overdueCount = grouped.overdue.length;

  const renderTaskCard = (task: Task, index: number, isCompleted = false) => {
    const lead = getLeadForTask(task);
    const banker = task.smContactId ? bankers.find(b => b.id === task.smContactId) : null;
    const partner = task.connectorId ? channelPartners.find(c => c.id === task.connectorId) : null;
    const urgency = getUrgencyGroup(task);
    const config = URGENCY_CONFIG[urgency];
    const relativeTime = getRelativeTime(task);
    const formattedDate = getFormattedDate(task);
    const formattedTime = getFormattedTime(task);
    
    const handleCallClick = (e: React.MouseEvent, phone: string) => {
      e.stopPropagation();
      window.location.href = `tel:${phone}`;
    };
    
    const handleWhatsAppClick = (e: React.MouseEvent, phone: string) => {
      e.stopPropagation();
      const n = phone.replace(/[^0-9]/g, '');
      window.open(`https://wa.me/${n.startsWith('91') && n.length > 10 ? n : '91' + n}`, '_blank');
    };

    return (
      <motion.div
        key={task.id}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ delay: index * 0.03 }}
        layout
        className={cn(
          "group relative rounded-xl border bg-card overflow-hidden cursor-pointer",
          "hover:shadow-lg transition-all duration-200",
          !isCompleted && "border-l-4",
          !isCompleted && config.borderColor,
          isCompleted && "opacity-70"
        )}
        onClick={() => setSelectedTask(task)}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            {/* Complete / Uncomplete checkbox + Attachment indicator */}
            <div className="flex flex-col items-center gap-1">
              {!isCompleted ? (
                <button
                  onClick={(e) => handleCompleteRequest(e, task.id)}
                  className={cn(
                    "mt-0.5 flex-shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center",
                    "hover:bg-success/20 hover:border-success transition-colors",
                    urgency === 'overdue' ? 'border-red-400' : urgency === 'today' ? 'border-orange-400' : 'border-muted-foreground/40'
                  )}
                >
                  <Check className="h-3 w-3 opacity-0 group-hover:opacity-50 transition-opacity" />
                </button>
              ) : (
                <button
                  onClick={(e) => handleUnmarkCompleteRequest(e, task.id)}
                  className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-success/20 border-2 border-success flex items-center justify-center hover:bg-success/30 transition-colors"
                >
                  <Check className="h-3 w-3 text-success" />
                </button>
              )}
              {/* Attachment count */}
              {task.attachments && task.attachments.length > 0 && (
                <div className="flex items-center gap-0.5 text-[10px] text-muted-foreground">
                  <Paperclip className="h-3 w-3" />
                  <span className="font-medium">{task.attachments.length}</span>
                </div>
              )}
            </div>
            
            {/* Content */}
            <div className="flex-1 min-w-0 space-y-2">
              {/* Full description text */}
              <p className={cn(
                "text-sm font-medium leading-relaxed",
                isCompleted && "line-through text-muted-foreground"
              )}>
                {task.description || task.title}
              </p>
              
              {/* Contact Information - Multiple Rows with Full Buttons */}
              {(lead || banker || partner) && (
                <div className="space-y-2">
                  {/* Customer Row */}
                  {lead && lead.mobile && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">C:</span>
                      <span className="text-xs font-bold">{getLegacyCustomerName(lead).split(' ')[0]}</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-3"
                        onClick={(e) => handleCallClick(e, lead.mobile)}
                      >
                        <Phone className="h-3.5 w-3.5 mr-1" />
                        Call
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        className="h-8 px-3"
                        onClick={(e) => handleWhatsAppClick(e, lead.mobile)}
                      >
                        <MessageCircle className="h-3.5 w-3.5 mr-1" />
                        WhatsApp
                      </Button>
                    </div>
                  )}
                  
                  {/* Banker Row */}
                  {banker && banker.phone && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">B:</span>
                      <span className="text-xs font-bold">{banker.name}</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-3"
                        onClick={(e) => handleCallClick(e, banker.phone)}
                      >
                        <Phone className="h-3.5 w-3.5 mr-1" />
                        Call
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        className="h-8 px-3"
                        onClick={(e) => handleWhatsAppClick(e, banker.phone)}
                      >
                        <MessageCircle className="h-3.5 w-3.5 mr-1" />
                        WhatsApp
                      </Button>
                    </div>
                  )}
                  
                  {/* Partner Row */}
                  {partner && partner.whatsappNumber && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground font-medium">V:</span>
                      <span className="text-xs font-bold">{partner.name}</span>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="h-8 px-3"
                        onClick={(e) => handleCallClick(e, partner.whatsappNumber)}
                      >
                        <Phone className="h-3.5 w-3.5 mr-1" />
                        Call
                      </Button>
                      <Button
                        variant="success"
                        size="sm"
                        className="h-8 px-3"
                        onClick={(e) => handleWhatsAppClick(e, partner.whatsappNumber)}
                      >
                        <MessageCircle className="h-3.5 w-3.5 mr-1" />
                        WhatsApp
                      </Button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Meta Row - Loan Type */}
              {lead?.loanType && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-normal">
                    {LOAN_TYPE_LABELS[lead.loanType] || lead.loanType}
                  </Badge>
                </div>
              )}
              
              {/* Time info */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {!isCompleted && (
                    <span className={cn("text-[11px] font-semibold", config.color)}>
                      {relativeTime}
                    </span>
                  )}
                  {formattedDate && (
                    <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {formattedDate}
                      {/* Only show time if not a "Today at" reminder (to avoid redundancy) */}
                      {formattedTime && !relativeTime.includes("Today at") && ` • ${formattedTime}`}
                      {getCreatorFirstName(task) && ` · ${getCreatorFirstName(task)}`}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            {/* Arrow */}
            <ChevronRight className="h-4 w-4 text-muted-foreground/30 flex-shrink-0 mt-0.5 group-hover:text-muted-foreground/60 transition-colors" />
          </div>
        </div>
      </motion.div>
    );
  };

  const renderUrgencyGroup = (group: UrgencyGroup) => {
    const groupTasks = grouped[group];
    if (groupTasks.length === 0) return null;
    const config = URGENCY_CONFIG[group];
    const Icon = config.icon;

    return (
      <div key={group} className="space-y-2">
        <div className="flex items-center gap-2 px-1 py-1">
          <Icon className={cn("h-4 w-4", config.color)} />
          <span className={cn("text-xs font-bold uppercase tracking-wider", config.color)}>
            {config.label}
          </span>
          <Badge className={cn("text-[10px] px-1.5 py-0 h-4", config.badgeVariant)}>
            {groupTasks.length}
          </Badge>
        </div>
        <div className="space-y-2">
          {groupTasks.map((task, i) => renderTaskCard(task, i))}
        </div>
      </div>
    );
  };

  return (
    <AppLayout>
      <div className="p-4 md:p-6 lg:p-8 pt-6 space-y-4 max-w-4xl mx-auto pb-24 md:pb-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Reminders</h1>
            <p className="text-muted-foreground text-sm">
              {pendingTasks.length} pending{overdueCount > 0 && ` • ${overdueCount} overdue`}
            </p>
          </div>
          {/* Add Reminder Button - matching Add Lead style */}
          <Button 
            onClick={() => setAddTaskOpen(true)}
            className="shrink-0 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-md hover:shadow-lg transition-all"
            size="sm"
          >
            <BellPlus className="h-4 w-4 mr-2" />
            <span>Add Reminder</span>
          </Button>
        </div>

        {/* Search + Filter Row */}
        <div className="flex items-center gap-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search reminders or customers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
          
          {/* Filter Dropdown - Right Side */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5 flex-shrink-0">
                <SlidersHorizontal className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">
                  {timeFilter === "all" ? "All" : 
                   timeFilter === "overdue" ? "Overdue" : 
                   timeFilter === "today" ? "Today" : 
                   timeFilter === "week" ? "This Week" : 
                   timeFilter === "dateRange" ? "Date Range" : 
                   "Upcoming"}
                </span>
                {timeFilter !== "all" && (
                  <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 ml-0.5">
                    1
                  </Badge>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => setTimeFilter("all")} className={cn(timeFilter === "all" && "bg-accent")}>
                All Reminders
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeFilter("overdue")} className={cn(timeFilter === "overdue" && "bg-accent")}>
                <AlertTriangle className="h-3.5 w-3.5 mr-2 text-red-500" />
                Overdue
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeFilter("today")} className={cn(timeFilter === "today" && "bg-accent")}>
                <Clock className="h-3.5 w-3.5 mr-2 text-orange-500" />
                Today
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeFilter("week")} className={cn(timeFilter === "week" && "bg-accent")}>
                <Calendar className="h-3.5 w-3.5 mr-2 text-blue-500" />
                This Week
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeFilter("upcoming")} className={cn(timeFilter === "upcoming" && "bg-accent")}>
                <CalendarClock className="h-3.5 w-3.5 mr-2 text-blue-500" />
                Upcoming
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTimeFilter("dateRange")} className={cn(timeFilter === "dateRange" && "bg-accent")}>
                <Calendar className="h-3.5 w-3.5 mr-2 text-blue-500" />
                Date Range
              </DropdownMenuItem>
              {timeFilter === "dateRange" && (
                <div className="px-2 py-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">Start Date</label>
                    <Input
                      type="date"
                      value={dateRangeStart}
                      onChange={(e) => setDateRangeStart(e.target.value)}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs text-muted-foreground">End Date</label>
                    <Input
                      type="date"
                      value={dateRangeEnd}
                      onChange={(e) => setDateRangeEnd(e.target.value)}
                      min={dateRangeStart || undefined}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Tabs: Pending / Completed */}
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "pending" | "completed")}>
          <TabsList className="grid w-full grid-cols-2 h-10">
            <TabsTrigger value="pending" className="text-sm data-[state=active]:bg-success data-[state=active]:text-success-foreground gap-1.5">
              <Bell className="h-4 w-4" />
              Pending
              {pendingTasks.length > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-0.5">
                  {pendingTasks.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="completed" className="text-sm data-[state=active]:bg-success data-[state=active]:text-success-foreground gap-1.5">
              <CheckCircle2 className="h-4 w-4" />
              Completed
              {completedTasks.length > 0 && (
                <Badge variant="secondary" className="text-[10px] px-1.5 py-0 h-4 ml-0.5">
                  {completedTasks.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Pending Tab */}
          <TabsContent value="pending" className="mt-4 space-y-4">
            {/* Owner Filter Toggle */}
            <ToggleGroup 
              type="single" 
              value={ownerFilter} 
              onValueChange={(v) => v && setOwnerFilter(v as OwnerFilter)}
              className="justify-start"
            >
              <ToggleGroupItem value="all" size="sm" className="text-xs px-3">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="personal" size="sm" className="text-xs px-3">
                <User className="h-3 w-3 mr-1" />
                Personal
              </ToggleGroupItem>
              <ToggleGroupItem value="team" size="sm" className="text-xs px-3">
                <Users2 className="h-3 w-3 mr-1" />
                Team
              </ToggleGroupItem>
            </ToggleGroup>
            
            {pendingTasks.length === 0 ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center justify-center py-16 text-center"
              >
                <div className="rounded-full bg-success/10 p-5 mb-4">
                  <Check className="h-10 w-10 text-success" />
                </div>
                <p className="text-base font-medium text-foreground">You're all caught up! 🎉</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {searchQuery ? "No reminders match your search" : "No pending reminders"}
                </p>
              </motion.div>
            ) : (
              <AnimatePresence mode="popLayout">
                {(["overdue", "today", "upcoming"] as UrgencyGroup[]).map(renderUrgencyGroup)}
              </AnimatePresence>
            )}
          </TabsContent>

          {/* Completed Tab */}
          <TabsContent value="completed" className="mt-4 space-y-4">
            {/* Owner Filter Toggle */}
            <ToggleGroup 
              type="single" 
              value={ownerFilter} 
              onValueChange={(v) => v && setOwnerFilter(v as OwnerFilter)}
              className="justify-start"
            >
              <ToggleGroupItem value="all" size="sm" className="text-xs px-3">
                All
              </ToggleGroupItem>
              <ToggleGroupItem value="personal" size="sm" className="text-xs px-3">
                <User className="h-3 w-3 mr-1" />
                Personal
              </ToggleGroupItem>
              <ToggleGroupItem value="team" size="sm" className="text-xs px-3">
                <Users2 className="h-3 w-3 mr-1" />
                Team
              </ToggleGroupItem>
            </ToggleGroup>
            
            {completedTasks.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <CheckCircle2 className="h-10 w-10 text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">
                  {searchQuery ? "No completed reminders match your search" : "No completed reminders yet"}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {completedTasks.map((task, index) => renderTaskCard(task, index, true))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Task Dialog */}
      <AddTaskDialog open={addTaskOpen} onOpenChange={setAddTaskOpen} />
      
      {/* View/Edit Task Dialog */}
      {selectedTask && (
        <ViewEditTaskDialog 
          open={!!selectedTask} 
          onOpenChange={(open) => !open && setSelectedTask(null)}
          task={selectedTask}
          onSave={async (updatedTask) => {
            await updateTask(updatedTask.id, updatedTask);
            setSelectedTask(null);
          }}
        />
      )}

      {/* Confirm Complete Dialog */}
      <AlertDialog open={confirmCompleteOpen} onOpenChange={setConfirmCompleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark as Completed?</AlertDialogTitle>
            <AlertDialogDescription>
              This reminder will be moved to the completed section.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmComplete} className="bg-success hover:bg-success/90">
              Complete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Confirm Uncomplete Dialog */}
      <AlertDialog open={confirmUncompleteOpen} onOpenChange={setConfirmUncompleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Move to Pending?</AlertDialogTitle>
            <AlertDialogDescription>
              This reminder will be moved back to pending.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmUncomplete}>
              Move to Pending
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
